import React, { useEffect, useState } from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import { toast } from 'react-hot-toast';
import { AE_BTN_SECONDARY, AE_CARD_COMPACT, AE_MUTED } from '@/components/assessment-engine/layout/aeTheme';
import { assessmentEngineService } from '@/services/assessmentEngineService';
import type { AeCampaign, AeCampaignFunnel, CreateCampaignInput } from '@/types/assessmentEngine';
import { CampaignSetupForm } from '../CampaignSetupForm';
import { CampaignInvitationsToolbar } from '../CampaignInviteListFields';
import { CampaignInviteeStatusTable } from '../CampaignInviteeStatusTable';
import { CampaignAddInvitesModal } from './CampaignAddInvitesModal';

interface InvitationsTabProps {
  campaign: AeCampaign;
  publicUrl: string;
  onSave: (patch: Partial<CreateCampaignInput>) => Promise<void>;
}

/** Campaign hub — distribution access settings, invite link, pool, and reminders. */
export const InvitationsTab: React.FC<InvitationsTabProps> = ({ campaign, publicUrl, onSave }) => {
  const campaignId = campaign.id;
  const campaignStatus = campaign.status;
  const { t } = useLanguage();
  const [funnel, setFunnel] = useState<AeCampaignFunnel | null>(null);
  const [loading, setLoading] = useState(true);
  const [reminderBusy, setReminderBusy] = useState(false);
  const [addInvitesOpen, setAddInvitesOpen] = useState(false);
  const [removingInviteId, setRemovingInviteId] = useState<string | null>(null);
  const [enablingInviteId, setEnablingInviteId] = useState<string | null>(null);
  const [distributionSaving, setDistributionSaving] = useState(false);

  const canManageInvites = campaign.status === 'draft' || campaign.status === 'open';
  const isDraft = campaign.status === 'draft';
  const isArchived = campaign.status === 'archived';
  const distributionReadOnly = !isDraft;
  const distributionMessagingEditable = !isArchived;

  const saveDistribution = async (
    payload: Partial<CreateCampaignInput>,
    options?: { silent?: boolean }
  ) => {
    setDistributionSaving(true);
    try {
      await onSave(payload);
      if (!options?.silent) {
        toast.success(t('assessmentEngine.hub.configSaved'));
      }
    } catch {
      toast.error(t('assessmentEngine.hub.configFailed'));
      throw new Error('config save failed');
    } finally {
      setDistributionSaving(false);
    }
  };

  const loadFunnel = async () => {
    const funnelData = await assessmentEngineService.getCampaignFunnel(campaignId);
    setFunnel(funnelData);
  };

  useEffect(() => {
    loadFunnel()
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [campaignId]);

  const inviteCount = funnel?.summary?.invited ?? funnel?.rows.length ?? 0;
  const showSendReminders = campaignStatus === 'open' && inviteCount > 0;

  const distributionCard = (
    <div className={AE_CARD_COMPACT}>
      <CampaignSetupForm
        mode="edit"
        campaign={campaign}
        hubSection="distribution"
        autoSave
        readOnly={distributionReadOnly}
        messagingEditable={distributionMessagingEditable}
        benchmarkEditable
        saving={distributionSaving}
        submitLabel={t('assessmentEngine.settings.save')}
        onSubmit={async (payload, _pending, options) => saveDistribution(payload, options)}
      />
    </div>
  );

  if (loading) {
    return (
      <div className="space-y-3">
        {distributionCard}
        <p className={`${AE_MUTED} p-4`}>{t('assessmentEngine.hub.loadingInvitations')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {addInvitesOpen && (
        <CampaignAddInvitesModal
          campaignId={campaignId}
          distributionMode={campaign.distribution_mode}
          onClose={() => setAddInvitesOpen(false)}
          onAdded={() => void loadFunnel()}
        />
      )}

      {distributionCard}

      <div className={`${AE_CARD_COMPACT} space-y-2`}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-[var(--color-surface-text)]">
            {t('assessmentEngine.hub.inviteeStatusTitle')}
          </h3>
          {showSendReminders && (
            <button
              type="button"
              disabled={reminderBusy}
              className={AE_BTN_SECONDARY}
              onClick={async () => {
                setReminderBusy(true);
                try {
                  const { sent } = await assessmentEngineService.sendCampaignFollowUp(
                    campaignId,
                    'non_complete'
                  );
                  toast.success(t('assessmentEngine.hub.remindersSent', { count: sent }));
                  await loadFunnel();
                } catch {
                  toast.error(t('assessmentEngine.hub.remindersFailed'));
                } finally {
                  setReminderBusy(false);
                }
              }}
            >
              {t('assessmentEngine.hub.sendReminders')}
            </button>
          )}
        </div>
        <CampaignInvitationsToolbar
          distributionMode={campaign.distribution_mode}
          publicUrl={publicUrl}
          canAddInvites={canManageInvites}
          onAddInvites={() => setAddInvitesOpen(true)}
        />
        <CampaignInviteeStatusTable
          rows={funnel?.rows ?? []}
          canManage={canManageInvites}
          removingInviteId={removingInviteId}
          enablingInviteId={enablingInviteId}
          onRemoveInvite={async (inviteId) => {
            setRemovingInviteId(inviteId);
            try {
              await assessmentEngineService.removeInvite(campaignId, inviteId);
              toast.success(t('assessmentEngine.hub.inviteRemoved'));
              await loadFunnel();
            } catch {
              toast.error(t('assessmentEngine.hub.inviteRemoveFailed'));
            } finally {
              setRemovingInviteId(null);
            }
          }}
          onEnableInvite={async (inviteId) => {
            setEnablingInviteId(inviteId);
            try {
              await assessmentEngineService.enableInvite(campaignId, inviteId);
              toast.success(t('assessmentEngine.hub.inviteEnabled'));
              await loadFunnel();
            } catch {
              toast.error(t('assessmentEngine.hub.inviteEnableFailed'));
            } finally {
              setEnablingInviteId(null);
            }
          }}
        />
      </div>
    </div>
  );
};
